import React, { useEffect, useMemo } from 'react';

import { useForm } from '@tanstack/react-form';
import { z } from 'zod';
import { Box } from '@/shared/ui';

import { useDashboard } from '@/features/dashboard/api';
import { DASHBOARD_NAME_MAX_LENGTH } from '@/features/dashboard/constants/const';
import { createNameSchema } from '@/features/dashboard/lib/validateName';
import { NameInput } from '@/shared/ui/primitives/NameInput';
import { useAppSelector } from '@/store/hooks';

import {
  selectDashboardName,
  selectInitialDashboardName
} from '@/features/dashboard/state/dashboardEdit.selectors';
import { useDashboardEditActions } from '../../hooks/useDashboardEditActions';

import type { StandardSchemaV1Issue } from '@tanstack/react-form';

const isStandardSchemaIssue = (error: unknown): error is StandardSchemaV1Issue => {
  return typeof error === 'object' && error !== null && 'message' in error;
};

const convertErrorsToStandardSchema = (
  errors: readonly unknown[]
): StandardSchemaV1Issue[] | undefined => {
  const standardErrors = errors.filter(isStandardSchemaIssue);
  return standardErrors.length > 0 ? standardErrors : undefined;
};

interface EditDashboardNameProps {
  onValidationChange?: (hasErrors: boolean) => void;
}

type DashboardNameField = {
  state: {
    meta: {
      errors: readonly unknown[];
    };
    value: string;
  };
  handleBlur: () => void;
  handleChange: (value: string) => void;
};

const EditDashboardNameFieldContent = ({
  field,
  onValidationChange,
  setDashboardName
}: {
  field: DashboardNameField;
  onValidationChange?: (hasErrors: boolean) => void;
  setDashboardName: (name: string) => void;
}) => {
  const hasErrors = field.state.meta.errors.length > 0;

  useEffect(() => {
    onValidationChange?.(hasErrors);
  }, [hasErrors, onValidationChange]);

  return (
    <Box
      sx={{
        position: 'relative',
        maxWidth: '255px',
        '& .MuiInputBase-root': {
          fontSize: '32px',
          lineHeight: '32px',
          fontWeight: 700
        },
        '& .MuiInputBase-input': {
          fontWeight: 700,
          padding: '7px 2px 7px 8px !important'
        }
      }}
    >
      <NameInput
        error={convertErrorsToStandardSchema(field.state.meta.errors)}
        maxLength={DASHBOARD_NAME_MAX_LENGTH}
        size={32}
        value={field.state.value}
        onBlur={() => {
          field.handleBlur();
          if (field.state.meta.errors.length === 0 && field.state.value.trim()) {
            const trimmed = field.state.value.trim();
            setDashboardName(trimmed);
          }
        }}
        onChange={(newValue: string) => {
          field.handleChange(newValue);
        }}
      />
    </Box>
  );
};

const EditDashboardName = ({ onValidationChange }: EditDashboardNameProps) => {
  const dashboardName = useAppSelector(selectDashboardName);
  const initialDashboardName = useAppSelector(selectInitialDashboardName);
  const { renameDashboard } = useDashboardEditActions();
  const { data: dashboardData } = useDashboard();

  // Get existing dashboard names (excluding the current dashboard being edited)
  const existingNames = useMemo(() => {
    if (!dashboardData) return [];
    return Object.keys(dashboardData)
      .filter(name => name !== initialDashboardName)
      .map(name => name.toLowerCase());
  }, [dashboardData, initialDashboardName]);

  // Create schema with existing names for validation
  const DashboardNameSchema = useMemo(
    () =>
      z.object({
        name: createNameSchema('Dashboard name', DASHBOARD_NAME_MAX_LENGTH, existingNames)
      }),
    [existingNames]
  );

  const form = useForm({
    defaultValues: {
      name: dashboardName
    },
    validators: {
      onChange: DashboardNameSchema
    }
  });

  // Synchronize form with Redux state when it's initialized or changed from outside
  useEffect(() => {
    if (dashboardName && !form.state.isDirty && form.state.values.name !== dashboardName) {
      form.reset({ name: dashboardName });
    }
  }, [dashboardName, form]);

  return (
    <form.Field name='name'>
      {field => (
        <EditDashboardNameFieldContent
          field={field}
          setDashboardName={name => renameDashboard(name)}
          onValidationChange={onValidationChange}
        />
      )}
    </form.Field>
  );
};

export default EditDashboardName;
